import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { FC, ReactNode } from 'react'

import LuneIntelligenceIcon from './LuneIntelligenceIcon'

export interface NumberStepsProps {
    steps: { type: 'inference' | 'explanation'; component: ReactNode }[]
    sx?: SxProps
}

const NumberCircle: FC<{ nr: number }> = ({ nr }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'Blue500',
                width: '24px',
                height: '24px',
                borderRadius: '12px',
            }}
        >
            <Typography
                variant={'body3'}
                sx={{
                    lineHeight: '20px',
                    color: `White`,
                    fontWeight: 700,
                }}
            >
                {nr}
            </Typography>
        </Box>
    )
}

const NumberSteps: FC<NumberStepsProps> = ({ steps, sx }) => {
    let stepCount = 1

    const stepsWithIcons = steps.map((step, index, arr) => {
        const prevStep = arr.at(index - 1)
        const twoStepsBack = arr.at(index - 2)

        const hideIcon = step.type === 'inference' && prevStep?.type === 'inference'

        // Connecting lines are drawn from the top of the next element upwards
        // If we are skipping item 2 in a list of 3, it's element 3 that needs to draw
        // the long line back up to element 1
        const addLongLine = prevStep?.type === 'inference' && twoStepsBack?.type === 'inference'

        const icon =
            step.type === 'explanation' ? (
                <NumberCircle nr={stepCount++} />
            ) : (
                <LuneIntelligenceIcon
                    sx={{
                        color: hideIcon ? 'transparent' : 'Blue500',
                        width: '24px',
                        height: '24px',
                    }}
                />
            )

        const lineStyles =
            index !== 0
                ? {
                      '&:after': {
                          content: '""',
                          width: '1px',
                          borderWidth: '0',
                          borderLeft: '1px solid',
                          borderColor: `Blue500`,
                          margin: 'auto',
                          position: 'absolute',
                          height: '32px',
                          top: '-36px',
                          ...(addLongLine && {
                              height: '64px',
                              top: '-70px',
                          }),
                      },
                  }
                : {}

        return {
            type: step.type,
            icon,
            component: step.component,
            lineStyles,
        }
    })

    return (
        <Box
            sx={{
                ...sx,
            }}
        >
            {stepsWithIcons.map((step, index) => (
                <Box key={index}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            p: 1,
                        }}
                    >
                        <Box
                            sx={{
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexDirection: 'row',
                                mr: '36px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                    position: 'relative',
                                    ...step.lineStyles,
                                }}
                            >
                                {step.icon}
                            </Box>
                        </Box>
                        {step.component}
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

export default NumberSteps
